'use client'

import React, { useEffect, useRef, useState } from 'react'
import type { PictureWallBlock as PictureWallProps, Media } from '@/payload-types'
import RichText from '@/components/RichText'
import { bgClass } from '@/utilities/bgClass'

/* ---------- helpers ---------- */
function useInView<T extends HTMLElement>(threshold = 0.12) {
  const ref = useRef<T | null>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    if (!ref.current) return
    const el = ref.current
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true)
            obs.unobserve(e.target)
          }
        })
      },
      { threshold },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return { ref, inView }
}

function ImgBox({
  media,
  altFallback = '',
  className = '',
  delayMs = 0,
}: {
  media?: Media
  altFallback?: string
  className?: string
  delayMs?: number
}) {
  const { ref, inView } = useInView<HTMLDivElement>(0.18)
  const alt = media?.alt || altFallback || ''
  return (
    <div
      ref={ref}
      className={[
        'relative overflow-hidden rounded-xl bg-neutral-200 dark:bg-neutral-800',
        'opacity-0 translate-y-4 transition',
        inView ? 'opacity-100 translate-y-0' : '',
        className,
      ].join(' ')}
      style={{ transitionDuration: '600ms', transitionDelay: `${delayMs}ms` }}
    >
      {media?.url ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={media.url}
          alt={alt}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      ) : null}
    </div>
  )
}

/* ---------- main block ---------- */
export const PictureWallBlock = (props: PictureWallProps & { id?: string }) => {
  const { id, introContent, background = 'none', rows = [] } = props

  return (
    <section id={id ? `block-${id}` : undefined} className={`${bgClass(background)} py-12 md:py-20`}>
      {/* Header (keep normal container width) */}
      {introContent ? (
        <div className="container mb-8 md:mb-10">
          <RichText
            className="ms-0 [&_h1]:text-mini-primary [&_h1]:dark:text-mini-dark-primary [&_p]:text-mini-secondary [&_p]:dark:text-mini-dark-secondary"
            data={introContent}
            enableGutter={false}
          />
        </div>
      ) : null}

      {/* Rows — FULL WIDTH with just side padding */}
      <div className="w-full max-w-none px-4 md:px-6 lg:px-8 space-y-6">
        {rows.map((row, idx) => {
          const leftTop = row?.leftTop as unknown as Media | undefined
          const leftBottom = row?.leftBottom as unknown as Media | undefined
          const center = row?.center as unknown as Media | undefined
          const rightTop = row?.rightTop as unknown as Media | undefined
          const rightBottom = row?.rightBottom as unknown as Media | undefined

          return (
            <div
              key={row?.id ?? idx}
              className="
                grid gap-3 sm:gap-4 md:gap-5
                grid-cols-1
                sm:grid-cols-12
              "
            >
              {/* Left: two stacked squares (span 3/12) */}
              <div className="sm:col-span-3 grid grid-cols-1 gap-3 sm:gap-4 md:gap-5">
                <ImgBox media={leftTop} altFallback="Picture wall left top" className="aspect-square" delayMs={0} />
                <ImgBox media={leftBottom} altFallback="Picture wall left bottom" className="aspect-square" delayMs={90} />
              </div>

              {/* Center: big square (span 6/12) */}
              <div className="sm:col-span-6">
                <ImgBox media={center} altFallback="Picture wall center" className="aspect-square" delayMs={180} />
              </div>

              {/* Right: two stacked squares (span 3/12) */}
              <div className="sm:col-span-3 grid grid-cols-1 gap-3 sm:gap-4 md:gap-5">
                <ImgBox media={rightTop} altFallback="Picture wall right top" className="aspect-square" delayMs={270} />
                <ImgBox media={rightBottom} altFallback="Picture wall right bottom" className="aspect-square" delayMs={360} />
              </div>

              {/* Optional caption spans full width beneath row */}
              {row?.caption ? (
                <div className="sm:col-span-12">
                  <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">{row.caption}</p>
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </section>
  )
}

export const PictureWall = PictureWallBlock
export default PictureWallBlock
